import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PRETRIP_SECTIONS, PRETRIP_CRANE_SECTION, PRETRIP_PHOTO_SIDES, pretripItems } from '../pretripChecklist';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}

export default function PretripInspection() {
  const navigate = useNavigate();
  const { worker } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [truckId, setTruckId] = useState('');
  const [statuses, setStatuses] = useState({});
  const [notes, setNotes] = useState({});
  const [photos, setPhotos] = useState({});
  const [certName, setCertName] = useState('');
  const [generalNote, setGeneralNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/pretrip-trucks').then(list => {
      const arr = Array.isArray(list) ? list : [];
      setTrucks(arr);
      const myName = `${worker?.firstName || ''} ${worker?.lastName || ''}`.trim().toLowerCase();
      const mine = myName && arr.find(t => (t.assigned_to || '').trim().toLowerCase() === myName);
      if (mine) setTruckId(mine.id);
    }).catch(() => {});
    setCertName(`${worker?.firstName || ''} ${worker?.lastName || ''}`.trim());
  }, []);

  const truck = trucks.find(t => t.id === truckId);
  const isCrane = !!(truck && Number(truck.is_crane));
  const sections = isCrane ? [...PRETRIP_SECTIONS, PRETRIP_CRANE_SECTION] : PRETRIP_SECTIONS;
  const defectCount = Object.values(statuses).filter(s => s === 'defect').length;

  function toggle(key) { setStatuses(s => ({ ...s, [key]: s[key] === 'defect' ? 'ok' : 'defect' })); }
  function setPhoto(side, file) { setPhotos(p => ({ ...p, [side]: file })); }

  async function submit() {
    if (!truckId) { setErr('Select your truck'); return; }
    if (!certName.trim()) { setErr('Enter your name to certify'); return; }
    setSubmitting(true); setErr('');
    try {
      const items = pretripItems(isCrane).map(it => ({ key: it.key, label: it.label, section: it.section, status: statuses[it.key] === 'defect' ? 'defect' : 'ok', note: notes[it.key] || '' }));
      const report = await apiFetch('/pretrip-reports', { method: 'POST', body: JSON.stringify({ equipment_id: truckId, report_date: new Date().toISOString().slice(0, 10), checklist: items, certified_name: certName.trim(), note: generalNote, is_crane: isCrane ? 1 : 0 }) });
      if (!report || !report.id) throw new Error('Save failed');
      const uploads = Object.entries(photos).filter(([, f]) => f).map(([side, file]) => {
        const fd = new FormData(); fd.append('file', file); fd.append('label', side);
        return fetch(`${API_BASE}/pretrip-reports/${report.id}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      });
      await Promise.all(uploads);
      setDone({ defects: defectCount });
    } catch (e) { setErr('Could not submit — try again'); } finally { setSubmitting(false); }
  }

  const inp = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '12px', fontSize: 16, width: '100%', boxSizing: 'border-box', background: 'var(--card, #fff)', color: 'var(--text)' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--gray-muted, #6b7280)', textTransform: 'uppercase', marginBottom: 6, display: 'block' };

  if (done) {
    return (
      <div className="page">
        <div className="top-bar"><div className="top-bar-wordmark">TITAN <span>ELECTRIC</span></div></div>
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 54, marginBottom: 12 }}>{done.defects ? '⚠️' : '✅'}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>Inspection submitted</div>
          <div style={{ fontSize: 14, color: 'var(--gray-muted)', marginBottom: 28 }}>
            {done.defects ? `${done.defects} defect(s) flagged — the office has been notified and the truck marked for repair.` : 'No defects — truck is good to go.'}
          </div>
          <button onClick={() => navigate('/dashboard')} style={{ width: '100%', maxWidth: 320, padding: 14, background: '#f97316', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="top-bar">
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Back</span>
        </button>
      </div>

      <div style={{ padding: '18px 16px 100px' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>🚛 Daily Pre-Trip</div>
        <div style={{ fontSize: 13, color: 'var(--gray-muted)', marginBottom: 18 }}>Walk around the truck. Everything's OK by default — just flag anything wrong.</div>

        {/* Truck */}
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Truck</label>
          <select value={truckId} onChange={e => setTruckId(e.target.value)} style={inp}>
            <option value="">— Select CDL truck —</option>
            {trucks.map(t => <option key={t.id} value={t.id}>{t.asset_number ? `${t.asset_number} · ` : ''}{t.name}</option>)}
          </select>
          {isCrane && <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fffbeb', display: 'inline-block', padding: '3px 10px', borderRadius: 10 }}>Crane / Derrick — includes pre-lift checks</div>}
        </div>

        {/* Photos */}
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Walk-around photos <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-muted)' }}>(optional)</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
            {PRETRIP_PHOTO_SIDES.map(side => (
              <label key={side} style={{ border: '1px dashed var(--border, #d1d5db)', borderRadius: 10, padding: 8, textAlign: 'center', cursor: 'pointer', background: photos[side] ? '#f0fdf4' : 'var(--card, #fff)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minHeight: 76, justifyContent: 'center' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setPhoto(side, e.target.files[0])} />
                <span style={{ fontSize: 20 }}>{photos[side] ? '✅' : '📷'}</span>
                <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 600 }}>{side}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Checklist */}
        {sections.map(sec => (
          <div key={sec.section} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: sec.section.startsWith('Pre-Lift') ? '#b45309' : 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{sec.section}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sec.items.map(it => {
                const defect = statuses[it.key] === 'defect';
                return (
                  <div key={it.key} style={{ border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '10px 12px', background: defect ? '#fef2f2' : 'var(--card, #fff)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 14, color: 'var(--text)', flex: 1 }}>{it.label}</span>
                      <button onClick={() => toggle(it.key)}
                        style={{ border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff', background: defect ? '#dc2626' : '#16a34a', minWidth: 78 }}>
                        {defect ? 'Defect' : 'OK'}
                      </button>
                    </div>
                    {defect && (
                      <input value={notes[it.key] || ''} onChange={e => setNotes(n => ({ ...n, [it.key]: e.target.value }))}
                        placeholder="What's wrong?" style={{ ...inp, marginTop: 8, fontSize: 14, padding: '10px' }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* General note */}
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Notes <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-muted)' }}>(optional)</span></label>
          <textarea value={generalNote} onChange={e => setGeneralNote(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Certify */}
        <div style={{ marginBottom: 18, background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14 }}>
          <label style={lbl}>Certification</label>
          <div style={{ fontSize: 13, color: 'var(--gray-muted)', marginBottom: 8 }}>I certify I inspected this vehicle and reported any defects.</div>
          <input value={certName} onChange={e => setCertName(e.target.value)} placeholder="Your full name" style={inp} />
        </div>

        {err && <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{err}</div>}
        <button onClick={submit} disabled={submitting}
          style={{ width: '100%', padding: 16, background: submitting ? '#d1b48f' : (defectCount ? '#dc2626' : '#16a34a'), color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          {submitting ? 'Submitting…' : defectCount ? `Submit — ${defectCount} defect(s)` : 'Submit — All OK'}
        </button>
      </div>
    </div>
  );
}
